import type {
  DecisionEngineJourneyDto,
  PdlAffordabilityJourneyDto,
  PdlExistingLoansDto,
} from '../api/admin';
import type { IncomeCalculationTransactions, IncomeCalcStream } from './incomeCalculationDerivation';

export type PdlIncomeStreamEvidence = {
  payor: string;
  payorType: string | null;
  monthlyAmount: number;
  frequency: string | null;
  isPrimary: boolean;
};

export type PdlIncomeEvidence = {
  totalMonthly: number | null;
  primaryPayor: string | null;
  streamCount: number;
  streams: PdlIncomeStreamEvidence[];
  deMonthlyIncome: number | null;
  incomeAligned: boolean | null;
};

export type PdlDebtEvidence = {
  monthlyDebtService: number;
  elodAvailable: boolean | null;
  paydayStacking: number;
  nsfCount90d: number;
  loanVelocity90d: number;
  hasInternalC4y: boolean;
  riskFlags: string[];
  /** Itemised obligations (orchestrator metadata) — null for pre-stamp applications. */
  pdlExistingLoans: PdlExistingLoansDto | null;
};

export type PdlDecisionEvidence = {
  income: PdlIncomeEvidence | null;
  debt: PdlDebtEvidence | null;
};

function fmtMoney(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function readMetricsPrimaryPayor(metrics: unknown): string | null {
  if (!metrics || typeof metrics !== 'object' || Array.isArray(metrics)) return null;
  const payor = (metrics as Record<string, unknown>).primaryPayor;
  return typeof payor === 'string' && payor.trim() ? payor.trim() : null;
}

function readMetricsMonthly(metrics: unknown): number | null {
  if (!metrics || typeof metrics !== 'object' || Array.isArray(metrics)) return null;
  const raw = (metrics as Record<string, unknown>).normalizedMonthlyIncome;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function mapStream(stream: IncomeCalcStream, primaryPayor: string | null): PdlIncomeStreamEvidence {
  const payor = stream.payor.trim() || 'Unknown payor';
  const primary = primaryPayor != null && payor.toLowerCase() === primaryPayor.toLowerCase();
  return {
    payor,
    payorType: stream.payorType?.trim() || null,
    monthlyAmount: stream.monthlyAmountNormalized,
    frequency: stream.normalization?.frequency?.trim() || null,
    isPrimary: primary,
  };
}

function buildDebtEvidence(
  pdl: PdlAffordabilityJourneyDto,
  pdlExistingLoans: PdlExistingLoansDto | null,
): PdlDebtEvidence {
  const monthlyDebtService = Math.max(0, (pdl.existingMonthlyDebtServiceCents ?? 0) / 100);
  const riskFlags: string[] = [];
  if (pdl.elodAvailable === false) riskFlags.push('ELOD unavailable — debt assumed $0');
  if (pdl.hasInternalC4YExposure) riskFlags.push('Cash4You payment on account');
  const stacking = pdl.paydayStackingCount ?? 0;
  if (stacking >= 4) riskFlags.push(`${stacking} payday lenders`);
  const nsf = pdl.debtPaymentNsfCount90d ?? 0;
  if (nsf > 0) riskFlags.push(`${nsf} returned payment(s) / 90d`);
  const velocity = pdl.loanVelocity90d ?? 0;
  if (velocity > 0) riskFlags.push(`${velocity} recent loan deposit(s) / 90d`);

  return {
    monthlyDebtService,
    elodAvailable: pdl.elodAvailable ?? null,
    paydayStacking: stacking,
    nsfCount90d: nsf,
    loanVelocity90d: velocity,
    hasInternalC4y: Boolean(pdl.hasInternalC4YExposure),
    riskFlags,
    pdlExistingLoans,
  };
}

export function buildPdlDecisionEvidence(input: {
  decisionEngine?: DecisionEngineJourneyDto | null;
  incomeAudit?: IncomeCalculationTransactions | null;
  incomeMetrics?: unknown;
}): PdlDecisionEvidence {
  const pdl = input.decisionEngine?.pdlAffordability;
  const deMonthly =
    pdl?.monthlyIncome ??
    input.decisionEngine?.monthlyIncome ??
    null;

  const metricsMonthly = readMetricsMonthly(input.incomeMetrics);
  const primaryPayor = readMetricsPrimaryPayor(input.incomeMetrics);
  const streams = (input.incomeAudit?.streams ?? [])
    .filter((s) => Number.isFinite(s.monthlyAmountNormalized) && s.monthlyAmountNormalized > 0)
    .map((s) => mapStream(s, primaryPayor));

  const streamTotal = streams.reduce((sum, s) => sum + s.monthlyAmount, 0);
  const totalMonthly = metricsMonthly ?? (streamTotal > 0 ? streamTotal : deMonthly);

  let incomeAligned: boolean | null = null;
  if (deMonthly != null && totalMonthly != null && deMonthly > 0 && totalMonthly > 0) {
    incomeAligned = Math.abs(deMonthly - totalMonthly) <= 1;
  }

  const income: PdlIncomeEvidence | null =
    totalMonthly != null || streams.length > 0
      ? {
          totalMonthly,
          primaryPayor,
          streamCount: streams.length,
          streams,
          deMonthlyIncome: deMonthly,
          incomeAligned,
        }
      : null;

  const debt = pdl
    ? buildDebtEvidence(pdl, input.decisionEngine?.pdlExistingLoans ?? null)
    : null;

  return { income, debt };
}

/** One-line collapsed summary for income tile. */
export function summarizePdlIncomeEvidence(income: PdlIncomeEvidence | null): string {
  if (!income?.totalMonthly) return 'Tap to load bank income streams';
  const total = fmtMoney(income.totalMonthly);
  if (income.streamCount > 0) {
    return `${total}/mo · ${income.streamCount} stream${income.streamCount === 1 ? '' : 's'} — tap for payors`;
  }
  if (income.primaryPayor) return `${total}/mo · tap for payor detail`;
  return `${total}/mo · tap for verification detail`;
}

/** One-line collapsed summary for debt / ELOD tile. */
export function summarizePdlDebtEvidence(debt: PdlDebtEvidence | null): string {
  if (!debt) return 'Tap for ELOD / debt detail';
  const pay = fmtMoney(debt.monthlyDebtService);
  if (debt.elodAvailable === false) return `${pay}/mo payments · ELOD missing — tap`;
  if (debt.riskFlags.length > 0) {
    return `${pay}/mo · ${debt.riskFlags.length} flag${debt.riskFlags.length === 1 ? '' : 's'} — tap`;
  }
  return `${pay}/mo loan payments · tap for ELOD`;
}
